// IO half of the shared Delivery / Collection Note code (the pure half is deliveryNoteDraft.ts): write the row, draw the PDF, store it.
// Used by the warehouse page (Inbound -> Notes) and by Job -> Transport, so "generate" is one implementation.
import { pdf } from '@react-pdf/renderer'
import * as Sentry from '@sentry/react'
import { supabase } from '../api/supabase'
import type { DeliveryNote } from '../pages/Warehouse/types'
import { DeliveryCollectionNotePDF, type DeliveryCollectionNoteData } from '../pages/Warehouse/Devannings/DeliveryCollectionNotePDF'

// Insert (no id yet: the database assigns id and serial_no) or update the row; returns the stored note.
export async function saveNoteRow(existingId: string | null, row: Record<string, unknown>): Promise<DeliveryNote> {
  if (existingId) {
    const { data, error } = await supabase.from('delivery_notes').update(row).eq('id', existingId).select().single()
    if (error) throw error
    return data as DeliveryNote
  }
  const { data, error } = await supabase.from('delivery_notes').insert(row).select().single()
  if (error) throw error
  return data as DeliveryNote
}

// Draws the PDF from `data`, stores it as <folder>/<filePrefix>-<serial>.pdf in the warehouse-notes bucket and only THEN marks the note as
// generated (pdf_url / issued_at / issued_by_*): a note must never look generated when its file was not stored.
// `issuedAt` is frozen by the caller at the moment of generating, so the PDF and the stored issued_at cannot drift apart.
export async function generateNotePdf(a: {
  saved: DeliveryNote
  data: DeliveryCollectionNoteData
  folder: string
  filePrefix: string
  issuedAt: string
  userId: string | null
  issuerName: string
}): Promise<string> {
  const { saved, data, folder, filePrefix, issuedAt } = a
  const blob = await pdf(DeliveryCollectionNotePDF({ note: data })).toBlob()
  const path = `${folder}/${filePrefix}-${saved.serial_no}.pdf`
  const { error: uploadError } = await supabase.storage
    .from('warehouse-notes')
    .upload(path, blob, { contentType: 'application/pdf', upsert: true, cacheControl: '0' })
  if (uploadError) {
    Sentry.captureException(uploadError, {
      tags: { feature: 'warehouse_note_pdf_upload' },
      extra: { noteId: saved.id, noteType: filePrefix, path },
    })
    // Do NOT proceed to write pdf_url/issued_at -- the note must not look "generated" when the PDF was never stored.
    throw new Error(`PDF upload failed, note not marked as generated, please retry -- ${uploadError.message}`)
  }
  const { data: { publicUrl } } = supabase.storage.from('warehouse-notes').getPublicUrl(path)
  const cacheBusted = `${publicUrl}?v=${Date.now()}`
  const { error: updateError } = await supabase.from('delivery_notes').update({
    pdf_url: cacheBusted,
    issued_at: issuedAt,
    issued_by_user_id: a.userId,
    issued_by_name: a.issuerName,
  }).eq('id', saved.id)
  if (updateError) {
    Sentry.captureException(updateError, {
      tags: { feature: 'warehouse_note_pdf_url_save' },
      extra: { noteId: saved.id, noteType: filePrefix, path },
    })
    throw new Error(`PDF generated but saving the record failed, please retry -- ${updateError.message}`)
  }
  return cacheBusted
}
